import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Building2, CheckCircle, AlertTriangle, XCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { JobInsiderReportDetail } from "@/components/JobInsiderReportDetail";

interface JobIntelReport {
  id: string;
  user_id: string;
  job_url: string | null;
  company_name: string | null;
  job_title: string | null;
  verdict: string | null;
  job_status: any;
  hiring_manager: any;
  pain_points: any;
  company_goals: any;
  resume_tips: string | null;
  report: string | null;
  status: string;
  error: string | null;
  message: string | null;
  created_at: string;
}

export default function JobIntelReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState<JobIntelReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<JobIntelReport | null>(null);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    fetchReports();
  }, [user, navigate]);

  const fetchReports = async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from('job_intel_reports')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching reports:', error);
    } else {
      setReports((data as JobIntelReport[]) || []);
    }
    setLoading(false);
  };

  const getVerdictBadge = (verdict: string | null) => {
    switch (verdict) {
      case 'APPLY':
        return <Badge className="bg-success text-success-foreground"><CheckCircle className="w-3 h-3 mr-1" /> Apply</Badge>;
      case 'CAUTION':
        return <Badge className="bg-warning text-warning-foreground"><AlertTriangle className="w-3 h-3 mr-1" /> Caution</Badge>;
      case 'DONT_APPLY':
        return <Badge className="bg-destructive text-destructive-foreground"><XCircle className="w-3 h-3 mr-1" /> Don't Apply</Badge>;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="outline" className="border-success text-success">Completed</Badge>;
      case 'failed':
        return <Badge variant="outline" className="border-destructive text-destructive">Failed</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (selectedReport) {
    return (
      <JobInsiderReportDetail 
        report={selectedReport} 
        onBack={() => setSelectedReport(null)} 
      />
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="bg-white">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        <div className="flex-1 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">JobIntel 360 Reports</h1>
            <p className="text-muted-foreground">Your comprehensive job intelligence reports</p>
          </div>
          <Button onClick={() => navigate('/job-intel')}>
            <Plus className="w-4 h-4 mr-2" />
            New Report
          </Button>
        </div>
      </div>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Reports Yet</h3>
            <p className="text-muted-foreground mb-4">Generate your first JobIntel 360 report to get started.</p>
            <Button onClick={() => navigate('/job-intel')}>
              <Plus className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <Card 
              key={report.id} 
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => setSelectedReport(report)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {report.company_name || 'Unknown Company'} - {report.job_title || 'Unknown Title'}
                    </CardTitle>
                    <CardDescription>
                      {new Date(report.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(report.status)}
                    {report.verdict && getVerdictBadge(report.verdict)}
        </div>
      </div>
              </CardHeader>
              {report.job_url && (
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground truncate">{report.job_url}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
